import React, { FC, useCallback, useEffect, useState } from 'react'
import styles from 'styles/components/template.module.scss'
import text from 'styles/utility/text.module.scss'
import startOfToday from 'date-fns/startOfToday'
import { Spacer } from 'components/Utility'
import { BlueButton, GreyButton } from 'components/Button'
import { MainHeadline, SubCaution, SubDescription } from 'components/Typography'
import { useDispatch, useSelector } from 'react-redux'
import { push } from 'connected-react-router'
import { AlertCard, CouponCard } from 'components/Card'
import { FormLabel } from 'components/Label'
import {
  Checkbox,
  DatetimePicker,
  RadioButtonsForm,
  SelectBox,
  StoreSelectForm,
  Textarea,
  TextInput,
} from 'components/Form'
import { useCheckbox, useNumberChangeEvent, useSelectCallback, useStringChangeEvent } from 'hooks/form'
import { FlexRow } from 'components/Flex'
import { couponDiscountTypeOptions } from 'constants/options'
import { parseDateToDatetimeLocal, parseDaysToSeconds, parseSecondsToDays } from 'lib/helper'
import { useSerialNumberOptions } from 'hooks/utility'
import { useHandleDiscountRate, useQueryCouponCodes, useSelectStore } from 'hooks/coupon'
import { State } from 'index'
import { getConfirmCoupon } from 'reducks/coupon/selectors'
import { FirebaseTimestamp } from 'firebase/index'
import { confirmCoupon } from 'reducks/coupon/operations'
import { CouponType } from 'types/coupon'
import { useQueryAllStores } from 'hooks/store'
import { CouponError } from 'components/Error'
import { isFilledCouponRequiredFields } from 'lib/judge'

const CouponCreateTemplate: FC = () => {
  const dispatch = useDispatch()
  const backToCouponPage = useCallback(() => dispatch(push('/admin/coupon')), [dispatch])
  const coupon: CouponType = useSelector((state: State) => getConfirmCoupon(state))

  const [name, setName] = useState<string>('')
  const [code, setCode] = useState<string>('')
  const [description, setDescription] = useState<string>('')
  const [discountType, setDiscountType] = useState<string>('flat_rate')
  const [discountAmount, setDiscountAmount] = useState<number>(0)
  const [discountRate, setDiscountRate] = useState<number>(0)
  const [maxDiscount, setMaxDiscount] = useState<number>(0)
  const [minAmount, setMinAmount] = useState<number>(0)
  const [expiresAt, setExpiresAt] = useState<string>('')
  const [expiresMaxage, setExpiresMaxage] = useState<string>('')
  const [maxTotal, setMaxTotal] = useState<number>(0)
  const [maxPerUser, setMaxPerUser] = useState<number>(0)
  const [isFirstLimited, setFirstLimited] = useState<string[]>([])
  const [isUserLimited, setUserLimited] = useState<string[]>([])
  const [isStoreLimited, setStoreLimited] = useState<string[]>([])
  const [addStore, removeStore, handleStores, setStoreIds, storeIds] = useSelectStore(
    coupon && coupon.targets && coupon.targets.stores ? coupon.targets.stores : []
  )

  const handleName = useStringChangeEvent(setName)
  const handleCode = useStringChangeEvent(setCode)
  const handleDescription = useStringChangeEvent(setDescription)
  const handleDiscountType = useStringChangeEvent(setDiscountType)
  const handleDiscountAmount = useNumberChangeEvent(setDiscountAmount)
  const handleDiscountRate = useHandleDiscountRate(setDiscountRate)
  const handleMaxDiscount = useNumberChangeEvent(setMaxDiscount)
  const handleMinAmount = useNumberChangeEvent(setMinAmount)
  const handleExpiresAt = useStringChangeEvent(setExpiresAt)
  const selectExpiresMaxage = useSelectCallback(setExpiresMaxage)
  const handleMaxTotal = useNumberChangeEvent(setMaxTotal)
  const handleMaxPerUser = useNumberChangeEvent(setMaxPerUser)
  const handleFirstLimited = useCheckbox(setFirstLimited)
  const handleUserLimited = useCheckbox(setUserLimited)
  const handleStoreLimited = useCheckbox(setStoreLimited)

  const saveConfirmCoupon = () => {
    const nextCoupon: CouponType = {
      name,
      description,
      code,
      discount_amount: discountAmount,
      discount_rate: discountRate,
      status: 'public',
      type: isUserLimited.includes('is_user_limited') ? 'manual' : 'request',
      limitations: {
        checkout_type: ['daily', 'preorder', 'delivery'],
        is_first: isFirstLimited.includes('is_first_limited'),
      },
    }
    // 任意項目の追加
    if (expiresAt !== '') nextCoupon.expires_at = FirebaseTimestamp.fromDate(new Date(expiresAt))
    if (expiresMaxage !== '') nextCoupon.expires_maxage = parseDaysToSeconds(Number(expiresMaxage))
    if (maxPerUser > 0) nextCoupon.limitations.max_per_user = maxPerUser
    if (maxTotal > 0) nextCoupon.limitations.max_total = maxTotal
    if (minAmount > 0) nextCoupon.limitations.min_amount = minAmount
    if (maxDiscount > 0) nextCoupon.limitations.max_discount = maxDiscount
    if (isStoreLimited.includes('is_store_limited')) nextCoupon.targets = { stores: storeIds }
    dispatch(confirmCoupon(nextCoupon))
  }

  const couponCodes = useQueryCouponCodes()
  const expiresMaxageOptions = useSerialNumberOptions('日後', 365, 1, '○日後')
  const stores = useQueryAllStores()
  const isFilledRequiredFields = isFilledCouponRequiredFields(
    name,
    code,
    couponCodes,
    description,
    discountAmount,
    discountRate,
    maxDiscount,
    minAmount,
    isStoreLimited.includes('is_store_limited') ? storeIds : undefined
  )

  // 確認画面から戻ってきたときの初期値をセットする
  useEffect(() => {
    if (!coupon) return
    setName(coupon.name)
    setCode(coupon.code)
    setDescription(coupon.description)
    setDiscountType(coupon.discount_rate > 0 ? 'percent' : 'flat_rate')
    setDiscountAmount(coupon.discount_amount)
    setDiscountRate(coupon.discount_rate)
    setMaxDiscount(coupon.limitations.max_discount || 0)
    setMinAmount(coupon.limitations.min_amount || 0)
    setExpiresAt(coupon.expires_at ? parseDateToDatetimeLocal(coupon.expires_at.toDate()) : '')
    setExpiresMaxage(coupon.expires_maxage ? String(parseSecondsToDays(coupon.expires_maxage)) : '')
    setMaxTotal(coupon.limitations.max_total || 0)
    setMaxPerUser(coupon.limitations.max_per_user || 0)
    setFirstLimited(coupon.limitations.is_first ? ['is_first_limited'] : [])
    setUserLimited(coupon.type === 'manual' ? ['is_user_limited'] : [])
    setStoreLimited(
      coupon.targets && coupon.targets.stores && coupon.targets.stores.length > 0 ? ['is_store_limited'] : []
    )
  }, [])

  return (
    <div className={styles.standard}>
      <Spacer size={28} />
      <MainHeadline headline={'新しくクーポンを登録する'} />
      <Spacer size={20} />
      <AlertCard
        headline={'一度登録したクーポンは、編集することはできないため注意して登録してください。'}
        title={'注意'}
      />
      <Spacer size={20} />
      <FormLabel label={'プレビュー'} required={false} />
      <Spacer size={8} />
      <CouponCard
        createdDate={startOfToday()}
        description={description}
        name={name}
        discountAmount={discountAmount}
        discountRate={discountRate}
        maxDiscount={maxDiscount}
        maxPerUser={maxPerUser}
        minAmount={minAmount}
        expiresAt={FirebaseTimestamp.fromDate(new Date(expiresAt))}
        expiresMaxage={parseDaysToSeconds(Number(expiresMaxage))}
        isFirst={isFirstLimited.includes('is_first_limited')}
        storeIds={storeIds}
        stores={stores}
      />
      <Spacer size={20} />
      <FormLabel id={'name'} label={'クーポン名'} required={true} />
      <Spacer size={4} />
      <SubDescription description={'アプリに表示される名前です。命名には気をつけてください。'} />
      <Spacer size={8} />
      <TextInput
        disabled={false}
        required={true}
        onChange={handleName}
        placeholder={'１周年記念クーポン'}
        size={'small'}
        type={'text'}
        value={name}
      />
      <Spacer size={20} />
      <FormLabel id={'code'} label={'クーポンコード'} required={false} />
      <Spacer size={4} />
      <SubDescription
        description={
          '入力しない場合コードは自動で発行されます。第三者が特定しやすいクーポンコードは入力しないでください。'
        }
      />
      <SubCaution caution={'※招待クーポンが8桁のため、8桁外で登録してください。'} />
      <Spacer size={8} />
      <TextInput
        disabled={false}
        maxLength={20}
        required={true}
        onChange={handleCode}
        placeholder={'SACRI_1ST_ANNIVERSARY'}
        size={'small'}
        type={'text'}
        value={code}
      />
      <Spacer size={12} />
      <FlexRow justifyContent={'end'}>
        <SubDescription description={`${code.length}/20`} />
      </FlexRow>
      <Spacer size={12} />
      <FormLabel id={'description'} label={'クーポン説明文'} required={true} />
      <Spacer size={4} />
      <SubDescription description={'お客さまへ説明するための文章です。クーポン画像の下部に反映されます。'} />
      <Spacer size={8} />
      <Textarea
        disabled={false}
        maxLength={100}
        required={true}
        rows={5}
        onChange={handleDescription}
        placeholder={'本クーポンは初回購入時限定です。500円（税込）以上の購入時にのみ適応されます。'}
        value={description}
      />
      <Spacer size={20} />
      <FormLabel label={'割引方法'} required={true} />
      <Spacer size={8} />
      <RadioButtonsForm
        currentStatus={discountType}
        options={couponDiscountTypeOptions}
        handleStatus={(e) => {
          setDiscountAmount(0)
          setDiscountRate(0)
          setMaxDiscount(0)
          handleDiscountType(e)
        }}
      />
      <Spacer size={20} />
      <FormLabel id={'discount-rate'} label={`割引${discountType === 'flat_rate' ? '金額' : '率'}`} required={true} />
      <Spacer size={8} />
      <FlexRow justifyContent={'start'}>
        <TextInput
          disabled={false}
          required={true}
          onChange={discountType === 'flat_rate' ? handleDiscountAmount : handleDiscountRate}
          size={'small'}
          type={'tel'}
          value={discountType === 'flat_rate' ? discountAmount : discountRate}
        />
        <Spacer size={8} />
        <span className={text.font_bold}>{discountType === 'flat_rate' ? '円' : '%'}</span>
        <Spacer size={8} />
      </FlexRow>
      {discountType === 'percent' && (
        <>
          <Spacer size={20} />
          <FormLabel id={'max-discount'} label={'割引額上限'} required={true} />
          <Spacer size={4} />
          <SubDescription description={'クーポンで割引する上限金額を入力してください。'} />
          <Spacer size={8} />
          <FlexRow justifyContent={'start'}>
            <TextInput
              disabled={false}
              required={true}
              onChange={handleMaxDiscount}
              size={'small'}
              type={'tel'}
              value={maxDiscount}
            />
            <Spacer size={8} />
            <span className={text.font_bold}>円</span>
            <Spacer size={8} />
          </FlexRow>
        </>
      )}
      <Spacer size={20} />
      <FormLabel id={'min-amount'} label={'最小決済金額（税込）'} required={false} />
      <Spacer size={4} />
      <SubDescription description={'お客さまがクーポンを利用できる最小決済金額を入力してください。'} />
      <Spacer size={8} />
      <FlexRow justifyContent={'start'}>
        <TextInput
          disabled={false}
          required={false}
          onChange={handleMinAmount}
          size={'small'}
          type={'tel'}
          value={minAmount}
        />
        <Spacer size={8} />
        <span className={text.font_bold}>円</span>
        <Spacer size={8} />
      </FlexRow>
      <Spacer size={54} />
      {/* クーポン有効期限 */}
      <FormLabel label={'クーポン有効期限'} required={false} />
      <Spacer size={4} />
      <SubDescription
        description={
          'お客さまがクーポンを利用できる期間です。この日時まで利用できる固定期限と、クーポンを取得してから利用できる相対期限それぞれを設定できます。\n' +
          '※どちらの期限も未入力の場合、無制限で取得と利用することができます。\n' +
          '※どちらの期限も設定した場合、固定期限と相対期限の早い方が期限として設定されます。'
        }
      />
      <Spacer size={20} />
      <FormLabel label={'固定期限'} required={false} size={'small'} />
      <Spacer size={8} />
      <SubDescription description={'この日時以降、お客さまがクーポンを利用することができなくなります'} />
      <Spacer size={8} />
      <DatetimePicker min={parseDateToDatetimeLocal(startOfToday())} onChange={handleExpiresAt} value={expiresAt} />
      <Spacer size={20} />
      <FormLabel label={'相対期限（任意）'} required={false} size={'small'} />
      <Spacer size={8} />
      <SubDescription description={'お客さまがクーポンを取得してからの期限です。'} />
      <Spacer size={8} />
      <SelectBox
        hasPlaceholder={true}
        options={expiresMaxageOptions}
        select={selectExpiresMaxage}
        value={expiresMaxage}
      />
      <Spacer size={20} />
      {/* クーポン配布上限 */}
      <FormLabel label={'クーポン配布上限'} required={false} />
      <Spacer size={4} />
      <SubDescription
        description={
          '取得できるクーポンの上限を入力してください。空白の場合、無制限で配布します\n' +
          '１ユーザーあたりの取得上限を設定した場合、設定した数だけ取得できるようになるため、ご注意ください。'
        }
      />
      <Spacer size={12} />
      <FormLabel label={'配布上限数'} required={false} size={'small'} />
      <Spacer size={8} />
      <TextInput
        disabled={false}
        required={false}
        onChange={handleMaxTotal}
        placeholder={'--'}
        size={'small'}
        type={'tel'}
        value={maxTotal || ''}
      />
      <Spacer size={12} />
      <FormLabel label={'1ユーザーあたりの取得上限'} required={false} size={'small'} />
      <Spacer size={8} />
      <TextInput
        disabled={false}
        required={false}
        onChange={handleMaxPerUser}
        placeholder={'1'}
        size={'small'}
        type={'tel'}
        value={maxPerUser || ''}
      />
      <Spacer size={20} />
      <FormLabel label={'取得条件'} required={false} />
      <Spacer size={4} />
      <SubDescription
        description={'クーポンを取得できるユーザーへ条件がある場合、該当する条件にチェックを入れてください。'}
      />
      <Spacer size={8} />
      <Checkbox
        fontSize={14}
        id={'is_first_limited'}
        isChecked={isFirstLimited.includes('is_first_limited')}
        onChange={handleFirstLimited}
        label={'初回購入限定'}
        size={'large'}
      />
      <Spacer size={24} />
      <Checkbox
        fontSize={14}
        id={'is_user_limited'}
        isChecked={isUserLimited.includes('is_user_limited')}
        onChange={handleUserLimited}
        label={'限定したユーザーへ配布\n（選択した場合、アプリ上からは取得できません）'}
        size={'large'}
      />
      <Spacer size={20} />
      <FormLabel id={'stores'} label={'利用条件'} required={false} />
      <Spacer size={4} />
      <SubDescription description={'クーポンを利用できる条件がある場合、該当する条件にチェックを入れてください。'} />
      <Spacer size={8} />
      <Checkbox
        fontSize={14}
        id={'is_store_limited'}
        isChecked={isStoreLimited.includes('is_store_limited')}
        onChange={(e) => {
          // 利用できる店舗を初期化する
          if (isStoreLimited.includes('is_store_limited')) {
            setStoreIds([])
          } else {
            setStoreIds([''])
          }
          handleStoreLimited(e)
        }}
        label={'利用できる店舗を制限する'}
        size={'large'}
      />
      {isStoreLimited.includes('is_store_limited') && (
        <>
          <Spacer size={24} />
          <SubDescription description={'選択された店舗のみ使用可能になります。'} />
          <Spacer size={24} />
          <StoreSelectForm
            addStore={addStore}
            removeStore={removeStore}
            handleStores={handleStores}
            storeIds={storeIds}
            stores={stores}
          />
        </>
      )}
      <Spacer size={20} />
      <BlueButton
        disabled={!isFilledRequiredFields}
        endIconPath={'/icon/white/arrow_next_small.svg'}
        label={'確認画面へ'}
        onClick={saveConfirmCoupon}
        size={'medium'}
        type={'main'}
      />
      <Spacer size={20} />
      <GreyButton label={'クーポン画面へ戻る'} onClick={backToCouponPage} size={'medium'} type={'outlined'} />
      <CouponError
        name={name}
        code={code}
        description={description}
        discountType={discountType}
        discountAmount={discountAmount}
        discountRate={discountRate}
        maxDiscount={maxDiscount}
        minAmount={minAmount}
        storeIds={storeIds}
        couponCodes={couponCodes}
      />
      <Spacer size={72} />
    </div>
  )
}

export default CouponCreateTemplate
